import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions, EventClickArg } from '@fullcalendar/core';
import esLocale from '@fullcalendar/core/locales/es';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { Subject, takeUntil } from 'rxjs';

import { environment } from '../../../../environments/environment.development';
import { SessionService } from '../../../auth/services/session/session.service';
import { CatalogsService } from '../../../shared/services/catalogs/catalogs.service';
import { LoadingService } from '../../../shared/services/loading/loading.service';
import type { Document as DocumentType } from '../../models/documents.model';
import type { Event } from '../../models/events.model';
import { SafeUrlPipe } from '../../pipes/safe-url/safe-url.pipe';
import { DocumentsService } from '../../services/documents/documents.service';
import { EventsService } from '../../services/events/events.service';

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [AccordionModule, ButtonModule, CommonModule, DialogModule, DropdownModule, FullCalendarModule, InputTextModule, ReactiveFormsModule, SafeUrlPipe],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss'
})
export class CalendarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fullCalendar') fullCalendar!: FullCalendarComponent;

  private readonly eventsService = inject(EventsService);
  private readonly documentsService = inject(DocumentsService);
  private readonly catalogsService = inject(CatalogsService);
  private readonly loadingService = inject(LoadingService);
  private readonly sessionService = inject(SessionService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  public readonly filterForm: FormGroup = this.formBuilder.group({
    search: [''],
    status__name: [''],
    event_type: [''],
    service_type: ['']
  });
  public statuses: any[] = [];
  public eventTypes: any[] = [];
  public serviceTypes: any[] = [];
  public events: Event[] = [];
  public documents: DocumentType[] = [];
  public selectedEvent: Event | null = null;
  public selectedDocument: DocumentType | null = null;
  public documentUrl: string | null = null;
  public isModalVisible = false;
  public iframeModalVisible = false;
  public isModalLoading = false;
  public isForCalendar = false;

  public calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    height: 'auto',
    initialView: 'timeGridWeek',
    weekends: true,
    locale: esLocale,
    timeZone: 'locale',
    slotMinTime: '07:00:00',
    slotMaxTime: '23:00:00',
    headerToolbar: { left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek' },
    buttonText: { today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día', list: 'Agenda' },
    dayMaxEvents: true,
    navLinks: true,
    views: {
      dayGridMonth: { dayMaxEventRows: 999, dayMaxEvents: 999, displayEventTime: false },
      listWeek: {
        listDayFormat: { weekday: 'long', day: 'numeric', month: 'long', omitCommas: true },
        listDaySideFormat: false,
        noEventsText: 'No hay eventos para este día',
        type: 'list',
        duration: { weeks: 1 }
      }
    },
    eventClick: (event) => this.handleEventClick(event),
    datesSet: (info) => this.loadEvents(info.startStr.split('T')[0], info.endStr.split('T')[0]),
    eventContent: (arg) => this.renderEventContent(arg)
  };

  ngOnInit(): void {
    this.catalogsService.getStatuses('EV').pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => this.statuses = this.asItems(response),
      error: () => this.statuses = []
    });
    this.catalogsService.getEventTypes().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => this.eventTypes = this.asItems(response),
      error: () => this.eventTypes = []
    });
    this.catalogsService.getServiceTypes().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => this.serviceTypes = this.asItems(response),
      error: () => this.serviceTypes = []
    });
  }

  ngAfterViewInit(): void {
    const api = this.fullCalendar?.getApi();
    if (api) {
      this.loadEvents(api.view.currentStart.toISOString().split('T')[0], api.view.currentEnd.toISOString().split('T')[0]);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyFilters(): void {
    const api = this.fullCalendar?.getApi();
    if (api) this.loadEvents(api.view.currentStart.toISOString().split('T')[0], api.view.currentEnd.toISOString().split('T')[0]);
  }

  resetFilters(): void {
    this.filterForm.reset({ search: '', status__name: '', event_type: '', service_type: '' });
    this.applyFilters();
  }

  loadEvents(initialDate?: string, finalDate?: string): void {
    this.loadingService.show();
    this.eventsService.getEvents({ ...this.filterForm.value, event_initial_date: initialDate, event_final_date: finalDate })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.events = this.asItems(response);
          const api = this.fullCalendar?.getApi();
          if (api) {
            api.removeAllEvents();
            api.addEventSource(this.eventSources);
          }
          this.loadingService.hide();
        },
        error: () => this.loadingService.hide()
      });
  }

  get eventSources(): any[] {
    return this.events.map((event: any) => ({
      id: `${event.id}`,
      title: `${event.title || event.folio} ${this.getStatusName(event)}`,
      start: `${event.initial_date}T${event.initial_hour}`,
      end: `${event.final_date}T${event.final_hour}`,
      backgroundColor: this.getBackgroundColor(event),
      borderColor: this.getBackgroundColor(event),
      extendedProps: {
        is_many: event.is_many,
        is_infinity: event.is_infinty,
        end_event: event.end_event,
        next_to_last: event.next_to_last,
        startTime: event.initial_hour,
        endTime: event.final_hour,
        isMultiEmployee: this.isEmployeeColorCase(event),
        employeeColors: this.getEmployeeColors(event)
      },
      className: 'recurring-event',
      display: 'block'
    }));
  }

  handleEventClick(clickInfo: EventClickArg): void {
    this.selectedEvent = this.events.find((event) => `${event.id}` === clickInfo.event.id) || null;
    if (!this.selectedEvent) return;
    this.documents = [];
    this.isModalLoading = true;
    this.isModalVisible = true;
    this.documentsService.getDocuments(this.selectedEvent.id).subscribe({
      next: (response) => {
        this.documents = response.data || [];
        this.isModalLoading = false;
      },
      error: () => this.isModalLoading = false
    });
  }

  renderEventContent(arg: any): { html: string } {
    const props = arg.event.extendedProps;
    const icon = `${props.is_many ? '🔄' : '📅'}${props.is_infinity ? '♾️' : ''}${props.end_event ? '🔚' : ''}${props.next_to_last ? '⏭️' : ''}`;
    const start = props.startTime?.substring(0, 5) || arg.timeText;
    const end = props.endTime?.substring(0, 5);
    const colors = (props.employeeColors || []).map((color: string) => `<span style="width:11px;height:11px;border-radius:50%;background:${color};border:1px solid rgba(0,0,0,.2);display:inline-block"></span>`).join('');
    const dots = colors ? `<span class="employee-dots">${colors}</span>` : '';
    const title = arg.event.title;
    if (arg.view.type === 'dayGridMonth') {
      return {
        html: `<div class="calendar-month-event" style="background-color:${arg.event.backgroundColor};border-color:${arg.event.borderColor};padding: 2px 4px;">
          <span>${start} ${icon} ${title}</span>
        </div>`
      };
    }

    if (arg.view.type === 'timeGridDay') {
      return { html: `<div class="calendar-event-detail"><strong>🕒 ${end ? `${start} - ${end}` : start}</strong><span>${icon} ${arg.event.title}</span>${dots}</div>` };
    }
    return { html: `<div class="calendar-event"><span>${start} ${icon} ${title}</span>${dots}</div>` };
  }

  getBackgroundColor(event: any): string {
    switch (event.status?.key_string) {
      case 'event-in-progress': return '#ffb700';
      case 'event-completed': return '#16A05A';
      case 'event-cancelled': return '#ef4444';
      default: return event.employee?.length > 1 ? '#3C5898cc' : event.employee?.[0]?.color || '#3C5898';
    }
  }

  getEmployeeColors(event: any): string[] {
    return (event.employee || []).filter((employee: any) => employee.color).map((employee: any) => employee.color);
  }

  isEmployeeColorCase(event: any): boolean {
    return !['event-in-progress', 'event-completed', 'event-cancelled'].includes(event.status?.key_string) && (event.employee || []).length > 0;
  }

  getStatusName(event: any): string {
    switch (event.status?.key_string) {
      case 'event-in-progress': return '(EN PROGRESO)';
      case 'event-completed': return '(COMPLETADO)';
      case 'event-cancelled': return '(CANCELADO)';
      case 'event-created': return '(PROGRAMADO)';
      default: return '(CONFIRMADO)';
    }
  }

  handleOpenDocument(document: DocumentType): void {
    if (this.sessionService.CompanyStatus !== 'company-paid') {
      window.open('https://pestforceapp.com/payment-overdue', '_blank');
      return;
    }
    this.selectedDocument = document;
    this.documentUrl = document.url.startsWith('http') ? document.url : environment.documentStorageUrl + document.url;
    this.isModalVisible = false;
    this.iframeModalVisible = true;
  }

  handleDownloadDocument(document: DocumentType): void {
    if (this.sessionService.CompanyStatus !== 'company-paid') {
      window.open('https://pestforceapp.com/payment-overdue', '_blank');
      return;
    }
    this.documentsService.getDocumentInStorage(document.url).subscribe((response) => {
      const url = response.data.startsWith('http') ? response.data : environment.documentStorageUrl + response.data;
      window.open(url, '_blank');
    });
  }

  handlePrintCalendar(): void {
    if (this.sessionService.CompanyStatus !== 'company-paid') {
      window.open('https://pestforceapp.com/payment-overdue', '_blank');
      return;
    }
    this.isForCalendar = true;
    this.isModalLoading = true;
    this.iframeModalVisible = true;
    this.documentsService.getCalendarDocument().subscribe({
      next: (response) => {
        this.documentUrl = environment.documentStorageUrl + response.data;
        this.isModalLoading = false;
      },
      error: () => this.isModalLoading = false
    });
  }

  private asItems(response: any): any[] {
    return Array.isArray(response) ? response : response?.items || response?.results || response?.data || [];
  }
}
